/**
 * @fileoverview capitulators.io API Client
 * Typed fetch wrappers for the capitulators.io data API.
 * 
 * Updated: December 2024 - Synced with production database (123 signals)
 */

const CONFIG = {
  // Use production API in production, localhost in development
  baseUrl: import.meta.env.PROD 
    ? 'https://data.capitulators.io/v1' 
    : 'http://localhost:8000/v1',
  timeout: 30000,
  // 24 hour cache - data updates daily via Airflow at 01:00 UTC
  cacheTTL: 24 * 60 * 60 * 1000,
};

const cache = new Map();

export class CapitulatorsAPIError extends Error {
  constructor(message, code, status, details = null) {
    super(message);
    this.name = 'CapitulatorsAPIError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

function getCached(key) {
  const cached = cache.get(key);
  if (!cached) return null;
  if (Date.now() - cached.timestamp > CONFIG.cacheTTL) {
    cache.delete(key);
    return null;
  }
  return cached.data;
}

function setCache(key, data) {
  cache.set(key, { data, timestamp: Date.now() });
}

export function clearCache(key) {
  if (key) cache.delete(key);
  else cache.clear();
}

async function fetchAPI(endpoint, options = {}) {
  const { method = 'GET', body, useCache = true } = options;
  const url = `${CONFIG.baseUrl}${endpoint}`;
  const cacheKey = `${method}:${url}:${body ? JSON.stringify(body) : ''}`;
  
  if (method === 'GET' && useCache) {
    const cached = getCached(cacheKey);
    if (cached) return cached;
  }
  
  const fetchOptions = {
    method,
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
  };
  
  if (body) fetchOptions.body = JSON.stringify(body);
  
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), CONFIG.timeout);
    fetchOptions.signal = controller.signal;
    
    const response = await fetch(url, fetchOptions);
    clearTimeout(timeoutId);
    
    const data = await response.json();
    
    if (!response.ok) {
      const error = data.error || { code: 'unknown_error', message: 'Unknown error occurred' };
      throw new CapitulatorsAPIError(error.message, error.code, response.status, error.details);
    }
    
    if (method === 'GET' && useCache) setCache(cacheKey, data);
    
    return data;
  } catch (error) {
    if (error instanceof CapitulatorsAPIError) throw error;
    if (error.name === 'AbortError') {
      throw new CapitulatorsAPIError('Request timed out', 'timeout', 408);
    }
    throw new CapitulatorsAPIError(error.message || 'Network error', 'network_error', 0);
  }
}

// ============================================================================
// SIGNAL ENDPOINTS
// ============================================================================

export async function fetchSignals() {
  const response = await fetchAPI('/signals');
  return response.data || [];
}

export async function fetchSignal(signalId) {
  const response = await fetchAPI(`/signals/${signalId}`);
  return response;
}

export async function fetchSignalHistory(signalId, options = {}) {
  const { days = 30 } = options;
  const response = await fetchAPI(`/signals/${signalId}/history?days=${days}`);
  return response;
}

export async function fetchSignalsBatch(signalIds) {
  const response = await fetchAPI('/signals/batch', {
    method: 'POST',
    body: { signal_ids: signalIds },
    useCache: false,
  });
  return response;
}

export async function fetchCatalog() {
  const response = await fetchAPI('/catalog/signals');
  return response.data || [];
}

export async function checkHealth() {
  return await fetchAPI('/health', { useCache: false });
}

// ============================================================================
// V2 ANALYTICS ENDPOINTS
// ============================================================================

/**
 * Fetches all signals' latest values in one call
 * @returns {Promise<{as_of: string, count: number, signals: Object[]}>}
 */
export async function fetchSignalsLatest() {
  return await fetchAPI('/signals/latest');
}

/**
 * Fetches data freshness/staleness info
 * @returns {Promise<{checked_at: string, summary: Object, signals: Object[]}>}
 */
export async function fetchSignalsFreshness() {
  return await fetchAPI('/signals/freshness');
}

/**
 * Fetches biggest movers (gainers/losers)
 * @param {Object} options
 * @param {string} [options.period='1d'] - Period: '1d', '7d', '30d'
 * @param {number} [options.limit=5] - Results per direction
 * @returns {Promise<{period: string, gainers: Object[], losers: Object[]}>}
 */
export async function fetchMovers(options = {}) {
  const { period = '1d', limit = 5 } = options;
  return await fetchAPI(`/signals/movers?period=${period}&limit=${limit}`);
}

/**
 * Fetches sparkline data for a signal
 * @param {string} signalId - Signal ID
 * @param {number} [points=30] - Number of data points (5-90)
 * @returns {Promise<{signal_id: string, points: number[], min: number, max: number, latest: number, change_pct: number}>}
 */
export async function fetchSparkline(signalId, points = 30) {
  return await fetchAPI(`/signals/${signalId}/sparkline?points=${points}`);
}

/**
 * Fetches signals ranked by a metric
 * @param {Object} options
 * @param {string} [options.by='z_score'] - Metric: 'z_score', 'percentile', 'value'
 * @param {string} [options.order='desc'] - Sort order: 'asc', 'desc'
 * @param {number} [options.limit=10] - Max results
 * @returns {Promise<{ranked_by: string, rankings: Object[]}>}
 */
export async function fetchRankings(options = {}) {
  const { by = 'z_score', order = 'desc', limit = 10 } = options;
  return await fetchAPI(`/signals/rankings?by=${by}&order=${order}&limit=${limit}`);
}

// ============================================================================
// SIGNAL DEFINITIONS - 123 Signals from Production Database
// Organized by category for UI display
// ============================================================================

export const SIGNAL_IDS = {
  // LIQUIDITY (12 signals) - Fed balance sheet, TGA, RRP, reserves
  liquidity: [
    'NET_LIQUIDITY', 'NET_LIQUIDITY_PCT', 'NET_LIQUIDITY_ROC', 'NET_LIQUIDITY_ZSCORE',
    'WALCL', 'WALCL_ROC', 'WALCL_ZSCORE',
    'WTREGEN', 'WTREGEN_ZSCORE',
    'RRPONTSYD', 'RRPONTSYD_ROC', 'RRPONTSYD_ZSCORE',
  ],
  
  // RATES (18 signals) - Treasury yields, curve spreads, real rates
  rates: [
    'DGS2', 'DGS2_ZSCORE', 'DGS3MO', 'DGS5', 'DGS7', 'DGS10', 'DGS10_PCT', 'DGS10_ZSCORE', 'DGS20', 'DGS30',
    'CURVE_2S10S', 'CURVE_2S10S_ZSCORE', 'CURVE_3M10Y', 'CURVE_3M10Y_ZSCORE',
    'REAL_5Y', 'REAL_10Y', 'REAL_10Y_ZSCORE',
    'TERM_PREMIUM_PROXY',
  ],
  
  // CREDIT (16 signals) - Corporate spreads, HY/IG, EM credit
  credit: [
    'BAMLH0A0HYM2', 'BAMLH0A0HYM2_PCT', 'BAMLH0A0HYM2_ZSCORE',
    'BAMLH0A2HYB', 'BAMLH0A3HYC', 'BAMLH0A3HYC_ZSCORE',
    'BAMLC0A0CM', 'BAMLC0A0CM_ZSCORE',
    'BAMLC0A4CBBB', 'BAMLC0A4CBBB_ZSCORE',
    'BAMLHE00EHYIOAS', 'BAMLEMCBPIOAS', 'BAMLEMCBPIOAS_ZSCORE',
    'HY_IG_SPREAD', 'HY_IG_SPREAD_ZSCORE',
    'CCC_B_SPREAD', 'CCC_B_SPREAD_ZSCORE', 'BBB_TREASURY_SPREAD',
  ],
  
  // FUNDING (10 signals) - Money market rates, repo, fed funds
  // Removed: TEDRATE, TEDRATE_ZSCORE (TED Spread requires LIBOR - discontinued)
  funding: [
    'SOFR', 'SOFR_ZSCORE', 'SOFR_EFFR_SPREAD', 'SOFR_EFFR_SPREAD_ZSCORE',
    'EFFR', 'EFFR_ZSCORE', 'EFFR_IORB_SPREAD',
    'IORB', 'OBFR',
    'DPRIME',
  ],
  
  // VOLATILITY (12 signals) - VIX, term structure, cross-asset vol
  // Removed: USD3MTD156N (3M LIBOR - discontinued June 2023)
  volatility: [
    'VIXCLS', 'VIXCLS_MA20', 'VIXCLS_PCT', 'VIXCLS_ZSCORE',
    'VIX_TERM_SPREAD', 'VIX_TERM_SPREAD_ZSCORE',
    'VXVCLS', 'VXVCLS_ZSCORE',
    'EVZCLS', 'EVZCLS_ZSCORE',
    'GVZCLS', 'OVXCLS',
  ],
  
  // MACRO (13 signals) - Financial conditions, sentiment, fed policy
  macro: [
    'NFCI', 'NFCI_ZSCORE', 'NFCICREDIT', 'NFCILEVERAGE', 'NFCIRISK',
    'ANFCI',
    'CFNAI',
    'STLFSI4', 'STLFSI4_ZSCORE',
    'UMCSENT', 'UMCSENT_ZSCORE',
    'DFEDTARL', 'DFEDTARU',
  ],
  
  // FX (12 signals) - Dollar indices, major pairs
  fx: [
    'DTWEXBGS', 'DTWEXBGS_PCT', 'DTWEXBGS_ROC', 'DTWEXBGS_ZSCORE',
    'DTWEXAFEGS', 'DTWEXAFEGS_ZSCORE',
    'DTWEXEMEGS', 'DTWEXEMEGS_ZSCORE',
    'DEXJPUS', 'DEXUSEU', 'DEXCHUS',
  ],
  
  // COMMODITIES (10 signals) - Oil, gas, energy
  // Removed: DPROPANEMBTX (no data), GASREGM (404 - doesn't exist in backend)
  commodities: [
    'DCOILWTICO', 'DCOILWTICO_MA50', 'DCOILWTICO_PCT', 'DCOILWTICO_ROC', 'DCOILWTICO_ZSCORE',
    'DCOILBRENTEU', 'DCOILBRENTEU_ZSCORE',
    'DHHNGSP', 'DHHNGSP_ROC', 'DHHNGSP_ZSCORE',
  ],
  
  // LABOR (6 signals) - Jobless claims
  labor: [
    'ICSA', 'ICSA_MA4', 'ICSA_ZSCORE',
    'CCSA', 'CCSA_ROC', 'CCSA_ZSCORE',
  ],
  
  // RESERVES (4 signals) - Bank reserves
  reserves: [
    'TOTRESNS', 'TOTRESNS_ZSCORE',
    'WLRRAL',
    'H41RESPPALDKNWW',
  ],
  
  // INFLATION (4 signals) - Breakevens, inflation expectations
  inflation: [
    'T5YIE', 'T5YIFR',
    'T10YIE', 'T10YIE_ZSCORE',
  ],
};

export const ALL_SIGNAL_IDS = Object.values(SIGNAL_IDS).flat();

export const CATEGORIES = {
  liquidity: { 
    name: 'Liquidity', 
    description: 'Fed balance sheet, TGA, reverse repo, and net liquidity metrics', 
    color: 'var(--tg-liquidity-cyan)', 
    icon: '💧',
    shortDesc: 'Dollar liquidity in the system'
  },
  rates: { 
    name: 'Rates', 
    description: 'Treasury yields, curve spreads, real rates, and term premia', 
    color: '#a78bfa', 
    icon: '📈',
    shortDesc: 'Interest rate signals'
  },
  credit: { 
    name: 'Credit', 
    description: 'Corporate spreads, high yield, investment grade, and EM credit', 
    color: 'var(--tg-rust-red)', 
    icon: '📉',
    shortDesc: 'Corporate bond stress'
  },
  funding: { 
    name: 'Funding', 
    description: 'SOFR, fed funds, repo rates, and money market spreads', 
    color: 'var(--tg-fog-green)', 
    icon: '🏦',
    shortDesc: 'Money market plumbing'
  },
  volatility: { 
    name: 'Volatility', 
    description: 'VIX, term structure, and cross-asset volatility measures', 
    color: 'var(--tg-lantern-gold)', 
    icon: '⚡',
    shortDesc: 'Market fear gauges'
  },
  macro: { 
    name: 'Macro', 
    description: 'Financial conditions indices, sentiment, and Fed policy', 
    color: '#60a5fa', 
    icon: '🌐',
    shortDesc: 'Big picture indicators'
  },
  fx: { 
    name: 'FX', 
    description: 'Dollar indices (broad, advanced, EM) and major currency pairs', 
    color: '#f472b6', 
    icon: '💱',
    shortDesc: 'Currency markets'
  },
  commodities: { 
    name: 'Commodities', 
    description: 'Crude oil (WTI, Brent), natural gas, and energy prices', 
    color: '#fbbf24', 
    icon: '🛢️',
    shortDesc: 'Energy markets'
  },
  labor: { 
    name: 'Labor', 
    description: 'Initial and continuing jobless claims', 
    color: '#34d399', 
    icon: '👷',
    shortDesc: 'Employment signals'
  },
  reserves: { 
    name: 'Reserves', 
    description: 'Bank reserves and Fed liabilities', 
    color: '#94a3b8', 
    icon: '🏛️',
    shortDesc: 'Banking system reserves'
  },
  inflation: { 
    name: 'Inflation', 
    description: 'Breakeven inflation rates and inflation expectations', 
    color: '#fb923c', 
    icon: '🔥',
    shortDesc: 'Inflation expectations'
  },
};

// Category order for display
export const CATEGORY_ORDER = [
  'liquidity', 'rates', 'credit', 'funding', 'volatility', 
  'macro', 'fx', 'commodities', 'labor', 'reserves', 'inflation'
];

/**
 * Signal metadata for enriching API responses
 * Maps signal IDs to human-readable names and descriptions
 */
export const SIGNAL_METADATA = {
  // Liquidity
  NET_LIQUIDITY: {
    name: 'Net Liquidity',
    description: 'Fed balance sheet minus TGA and reverse repo. Primary measure of dollar liquidity.',
    formula: 'WALCL - WTREGEN - RRPONTSYD',
    category: 'liquidity',
    tier: 'free',
    units: 'B',
  },
  NET_LIQUIDITY_ZSCORE: { name: 'Net Liquidity Z-Score', category: 'liquidity', units: 'σ' },
  NET_LIQUIDITY_PCT: { name: 'Net Liquidity Percentile', category: 'liquidity', units: '%' },
  NET_LIQUIDITY_ROC: { name: 'Net Liquidity Rate of Change', category: 'liquidity', units: '%' },
  WALCL: { name: 'Fed Total Assets', description: 'Federal Reserve total assets', category: 'liquidity', units: 'M' },
  WALCL_ROC: { name: 'Fed Assets Rate of Change', category: 'liquidity', units: '%' },
  WALCL_ZSCORE: { name: 'Fed Assets Z-Score', category: 'liquidity', units: 'σ' },
  WTREGEN: { name: 'Treasury General Account', description: 'Treasury cash balance at the Fed', category: 'liquidity', units: 'M' },
  WTREGEN_ZSCORE: { name: 'TGA Z-Score', category: 'liquidity', units: 'σ' },
  RRPONTSYD: { name: 'Reverse Repo', description: 'Overnight reverse repurchase agreements', category: 'liquidity', units: 'B' },
  RRPONTSYD_ROC: { name: 'RRP Rate of Change', category: 'liquidity', units: '%' },
  RRPONTSYD_ZSCORE: { name: 'RRP Z-Score', category: 'liquidity', units: 'σ' },
  
  // Rates
  CURVE_2S10S: { name: '2s10s Spread', description: '10Y minus 2Y Treasury yield', category: 'rates', units: '%' },
  CURVE_2S10S_ZSCORE: { name: '2s10s Z-Score', category: 'rates', units: 'σ' },
  CURVE_3M10Y: { name: '3M10Y Spread', description: '10Y minus 3M Treasury yield', category: 'rates', units: '%' },
  CURVE_3M10Y_ZSCORE: { name: '3M10Y Z-Score', category: 'rates', units: 'σ' },
  DGS2: { name: '2-Year Treasury', category: 'rates', units: '%' },
  DGS2_ZSCORE: { name: '2Y Treasury Z-Score', category: 'rates', units: 'σ' },
  DGS3MO: { name: '3-Month Treasury', category: 'rates', units: '%' },
  DGS5: { name: '5-Year Treasury', category: 'rates', units: '%' },
  DGS7: { name: '7-Year Treasury', category: 'rates', units: '%' },
  DGS10: { name: '10-Year Treasury', category: 'rates', units: '%' },
  DGS10_PCT: { name: '10Y Treasury Percentile', category: 'rates', units: '%' },
  DGS10_ZSCORE: { name: '10Y Treasury Z-Score', category: 'rates', units: 'σ' },
  DGS20: { name: '20-Year Treasury', category: 'rates', units: '%' },
  DGS30: { name: '30-Year Treasury', category: 'rates', units: '%' },
  REAL_5Y: { name: '5Y Real Rate', description: '5-Year TIPS yield', category: 'rates', units: '%' },
  REAL_10Y: { name: '10Y Real Rate', description: '10-Year TIPS yield', category: 'rates', units: '%' },
  REAL_10Y_ZSCORE: { name: '10Y Real Rate Z-Score', category: 'rates', units: 'σ' },
  TERM_PREMIUM_PROXY: { name: 'Term Premium Proxy', category: 'rates', units: '%' },
  
  // Credit
  BAMLH0A0HYM2: { name: 'HY OAS', description: 'ICE BofA US High Yield OAS', category: 'credit', units: 'bps' },
  BAMLH0A0HYM2_PCT: { name: 'HY OAS Percentile', category: 'credit', units: '%' },
  BAMLH0A0HYM2_ZSCORE: { name: 'HY OAS Z-Score', category: 'credit', units: 'σ' },
  BAMLH0A2HYB: { name: 'BB OAS', description: 'ICE BofA BB US High Yield OAS', category: 'credit', units: 'bps' },
  BAMLH0A3HYC: { name: 'CCC OAS', description: 'ICE BofA CCC US High Yield OAS', category: 'credit', units: 'bps' },
  BAMLH0A3HYC_ZSCORE: { name: 'CCC OAS Z-Score', category: 'credit', units: 'σ' },
  BAMLC0A0CM: { name: 'IG OAS', description: 'ICE BofA US Corporate OAS', category: 'credit', units: 'bps' },
  BAMLC0A0CM_ZSCORE: { name: 'IG OAS Z-Score', category: 'credit', units: 'σ' },
  BAMLC0A4CBBB: { name: 'BBB OAS', description: 'ICE BofA BBB US Corporate OAS', category: 'credit', units: 'bps' },
  BAMLC0A4CBBB_ZSCORE: { name: 'BBB OAS Z-Score', category: 'credit', units: 'σ' },
  BAMLHE00EHYIOAS: { name: 'Euro HY OAS', description: 'ICE BofA Euro High Yield OAS', category: 'credit', units: 'bps' },
  BAMLEMCBPIOAS: { name: 'EM Corporate OAS', description: 'ICE BofA EM Corporate OAS', category: 'credit', units: 'bps' },
  BAMLEMCBPIOAS_ZSCORE: { name: 'EM Corporate Z-Score', category: 'credit', units: 'σ' },
  HY_IG_SPREAD: { name: 'HY-IG Spread', description: 'High Yield minus Investment Grade spread', category: 'credit', units: 'bps' },
  HY_IG_SPREAD_ZSCORE: { name: 'HY-IG Spread Z-Score', category: 'credit', units: 'σ' },
  CCC_B_SPREAD: { name: 'CCC-B Spread', description: 'CCC minus B-rated spread', category: 'credit', units: 'bps' },
  CCC_B_SPREAD_ZSCORE: { name: 'CCC-B Spread Z-Score', category: 'credit', units: 'σ' },
  BBB_TREASURY_SPREAD: { name: 'BBB-Treasury Spread', category: 'credit', units: 'bps' },
  
  // Funding
  SOFR: { name: 'SOFR', description: 'Secured Overnight Financing Rate', category: 'funding', units: '%' },
  SOFR_ZSCORE: { name: 'SOFR Z-Score', category: 'funding', units: 'σ' },
  SOFR_EFFR_SPREAD: { name: 'SOFR-EFFR Spread', category: 'funding', units: 'bps' },
  SOFR_EFFR_SPREAD_ZSCORE: { name: 'SOFR-EFFR Z-Score', category: 'funding', units: 'σ' },
  EFFR: { name: 'Effective Fed Funds', description: 'Effective Federal Funds Rate', category: 'funding', units: '%' },
  EFFR_ZSCORE: { name: 'EFFR Z-Score', category: 'funding', units: 'σ' },
  EFFR_IORB_SPREAD: { name: 'EFFR-IORB Spread', category: 'funding', units: 'bps' },
  IORB: { name: 'Interest on Reserves', description: 'Interest Rate on Reserve Balances', category: 'funding', units: '%' },
  OBFR: { name: 'Overnight Bank Funding', description: 'Overnight Bank Funding Rate', category: 'funding', units: '%' },
  // Removed: TEDRATE, TEDRATE_ZSCORE (TED Spread - discontinued, required LIBOR)
  DPRIME: { name: 'Prime Rate', description: 'Bank Prime Loan Rate', category: 'funding', units: '%' },
  
  // Volatility
  VIXCLS: { name: 'VIX', description: 'CBOE Volatility Index', category: 'volatility', units: '' },
  VIXCLS_MA20: { name: 'VIX 20-Day MA', category: 'volatility', units: '' },
  VIXCLS_PCT: { name: 'VIX Percentile', category: 'volatility', units: '%' },
  VIXCLS_ZSCORE: { name: 'VIX Z-Score', category: 'volatility', units: 'σ' },
  VIX_TERM_SPREAD: { name: 'VIX Term Spread', description: 'VIX3M minus VIX', category: 'volatility', units: '' },
  VIX_TERM_SPREAD_ZSCORE: { name: 'VIX Term Z-Score', category: 'volatility', units: 'σ' },
  VXVCLS: { name: 'VVIX', description: 'VIX of VIX', category: 'volatility', units: '' },
  VXVCLS_ZSCORE: { name: 'VVIX Z-Score', category: 'volatility', units: 'σ' },
  EVZCLS: { name: 'Euro FX Vol', description: 'CBOE Euro Currency Volatility', category: 'volatility', units: '' },
  EVZCLS_ZSCORE: { name: 'Euro Vol Z-Score', category: 'volatility', units: 'σ' },
  GVZCLS: { name: 'Gold Vol', description: 'CBOE Gold Volatility', category: 'volatility', units: '' },
  OVXCLS: { name: 'Oil Vol', description: 'CBOE Crude Oil Volatility', category: 'volatility', units: '' },
  // Removed: USD3MTD156N (3M USD LIBOR - discontinued June 2023)
  
  // Macro
  NFCI: { name: 'NFCI', description: 'Chicago Fed National Financial Conditions Index', category: 'macro', units: '' },
  NFCI_ZSCORE: { name: 'NFCI Z-Score', category: 'macro', units: 'σ' },
  NFCICREDIT: { name: 'NFCI Credit', description: 'NFCI Credit Subindex', category: 'macro', units: '' },
  NFCILEVERAGE: { name: 'NFCI Leverage', description: 'NFCI Leverage Subindex', category: 'macro', units: '' },
  NFCIRISK: { name: 'NFCI Risk', description: 'NFCI Risk Subindex', category: 'macro', units: '' },
  ANFCI: { name: 'Adjusted NFCI', description: 'Adjusted National Financial Conditions Index', category: 'macro', units: '' },
  CFNAI: { name: 'CFNAI', description: 'Chicago Fed National Activity Index', category: 'macro', units: '' },
  STLFSI4: { name: 'St. Louis FSI', description: 'St. Louis Fed Financial Stress Index', category: 'macro', units: '' },
  STLFSI4_ZSCORE: { name: 'St. Louis FSI Z-Score', category: 'macro', units: 'σ' },
  UMCSENT: { name: 'Consumer Sentiment', description: 'U of Michigan Consumer Sentiment', category: 'macro', units: '' },
  UMCSENT_ZSCORE: { name: 'Sentiment Z-Score', category: 'macro', units: 'σ' },
  DFEDTARL: { name: 'Fed Funds Target Low', description: 'Fed Funds Target Range Lower', category: 'macro', units: '%' },
  DFEDTARU: { name: 'Fed Funds Target High', description: 'Fed Funds Target Range Upper', category: 'macro', units: '%' },
  
  // FX
  DTWEXBGS: { name: 'Dollar Index (Broad)', description: 'Trade-Weighted Dollar Index (Broad)', category: 'fx', units: '' },
  DTWEXBGS_PCT: { name: 'Dollar Percentile', category: 'fx', units: '%' },
  DTWEXBGS_ROC: { name: 'Dollar Rate of Change', category: 'fx', units: '%' },
  DTWEXBGS_ZSCORE: { name: 'Dollar Z-Score', category: 'fx', units: 'σ' },
  DTWEXAFEGS: { name: 'Dollar (Advanced)', description: 'Trade-Weighted Dollar vs Advanced Economies', category: 'fx', units: '' },
  DTWEXAFEGS_ZSCORE: { name: 'Dollar Advanced Z-Score', category: 'fx', units: 'σ' },
  DTWEXEMEGS: { name: 'Dollar (EM)', description: 'Trade-Weighted Dollar vs Emerging Markets', category: 'fx', units: '' },
  DTWEXEMEGS_ZSCORE: { name: 'Dollar EM Z-Score', category: 'fx', units: 'σ' },
  DEXJPUS: { name: 'USD/JPY', description: 'US Dollar to Japanese Yen', category: 'fx', units: '' },
  DEXUSEU: { name: 'EUR/USD', description: 'Euro to US Dollar', category: 'fx', units: '' },
  DEXCHUS: { name: 'USD/CNY', description: 'US Dollar to Chinese Yuan', category: 'fx', units: '' },
  
  // Commodities
  DCOILWTICO: { name: 'WTI Crude', description: 'West Texas Intermediate Crude Oil', category: 'commodities', units: '$/bbl' },
  DCOILWTICO_MA50: { name: 'WTI 50-Day MA', category: 'commodities', units: '$/bbl' },
  DCOILWTICO_PCT: { name: 'WTI Percentile', category: 'commodities', units: '%' },
  DCOILWTICO_ROC: { name: 'WTI Rate of Change', category: 'commodities', units: '%' },
  DCOILWTICO_ZSCORE: { name: 'WTI Z-Score', category: 'commodities', units: 'σ' },
  DCOILBRENTEU: { name: 'Brent Crude', description: 'Brent Crude Oil Price', category: 'commodities', units: '$/bbl' },
  DCOILBRENTEU_ZSCORE: { name: 'Brent Z-Score', category: 'commodities', units: 'σ' },
  DHHNGSP: { name: 'Natural Gas', description: 'Henry Hub Natural Gas Spot Price', category: 'commodities', units: '$/MMBtu' },
  DHHNGSP_ROC: { name: 'Nat Gas Rate of Change', category: 'commodities', units: '%' },
  DHHNGSP_ZSCORE: { name: 'Nat Gas Z-Score', category: 'commodities', units: 'σ' },
  // Removed: DPROPANEMBTX (no data), GASREGM (404)
  
  // Labor
  ICSA: { name: 'Initial Claims', description: 'Initial Jobless Claims', category: 'labor', units: 'K' },
  ICSA_MA4: { name: 'Claims 4-Week Avg', description: '4-Week Moving Average of Initial Claims', category: 'labor', units: 'K' },
  ICSA_ZSCORE: { name: 'Initial Claims Z-Score', category: 'labor', units: 'σ' },
  CCSA: { name: 'Continuing Claims', description: 'Continuing Jobless Claims', category: 'labor', units: 'K' },
  CCSA_ROC: { name: 'Continuing Claims ROC', category: 'labor', units: '%' },
  CCSA_ZSCORE: { name: 'Continuing Claims Z-Score', category: 'labor', units: 'σ' },
  
  // Reserves
  TOTRESNS: { name: 'Total Reserves', description: 'Total Reserve Balances', category: 'reserves', units: 'B' },
  TOTRESNS_ZSCORE: { name: 'Reserves Z-Score', category: 'reserves', units: 'σ' },
  WLRRAL: { name: 'Reserve Balances', description: 'Reserve Balances with Fed Banks', category: 'reserves', units: 'M' },
  H41RESPPALDKNWW: { name: 'Fed Liabilities', description: 'Fed Reserve Bank Credit', category: 'reserves', units: 'M' },
  
  // Inflation
  T5YIE: { name: '5Y Breakeven', description: '5-Year Breakeven Inflation Rate', category: 'inflation', units: '%' },
  T5YIFR: { name: '5Y5Y Forward', description: '5-Year, 5-Year Forward Inflation Expectation', category: 'inflation', units: '%' },
  T10YIE: { name: '10Y Breakeven', description: '10-Year Breakeven Inflation Rate', category: 'inflation', units: '%' },
  T10YIE_ZSCORE: { name: '10Y Breakeven Z-Score', category: 'inflation', units: 'σ' },
};

// ============================================================================
// SIGNAL CLASSIFICATION - CAP Computed vs Raw FRED
// ============================================================================

/**
 * Signal classification for visual hierarchy
 * Derived: Our calculations, transforms, and composites built from FRED data
 * Source: Raw data pulled directly from FRED API
 */
export const SIGNAL_CLASSIFICATION = {
  // Derived signals - calculated from source data
  cap_computed: [
    // Composite (proprietary formulas)
    'NET_LIQUIDITY', 'NET_LIQUIDITY_PCT', 'NET_LIQUIDITY_ROC', 'NET_LIQUIDITY_ZSCORE',
    'REAL_5Y', 'REAL_10Y', 'REAL_10Y_ZSCORE',
    'TERM_PREMIUM_PROXY',
    'VIX_TERM_SPREAD', 'VIX_TERM_SPREAD_ZSCORE',
    
    // Spreads (calculated)
    'CURVE_2S10S', 'CURVE_2S10S_ZSCORE',
    'CURVE_3M10Y', 'CURVE_3M10Y_ZSCORE',
    'HY_IG_SPREAD', 'HY_IG_SPREAD_ZSCORE',
    'CCC_B_SPREAD', 'CCC_B_SPREAD_ZSCORE',
    'BBB_TREASURY_SPREAD',
    'SOFR_EFFR_SPREAD', 'SOFR_EFFR_SPREAD_ZSCORE',
    'EFFR_IORB_SPREAD',
    
    // Z-Scores (statistical transforms)
    'WALCL_ZSCORE', 'WTREGEN_ZSCORE',
    'RRPONTSYD_ZSCORE',
    'DGS2_ZSCORE', 'DGS10_ZSCORE',
    'VIXCLS_ZSCORE', 'VXVCLS_ZSCORE', 'EVZCLS_ZSCORE',
    'BAMLH0A0HYM2_ZSCORE', 'BAMLH0A3HYC_ZSCORE', 'BAMLC0A0CM_ZSCORE', 'BAMLC0A4CBBB_ZSCORE',
    'BAMLEMCBPIOAS_ZSCORE', 'DCOILWTICO_ZSCORE', 'DCOILBRENTEU_ZSCORE',
    'DHHNGSP_ZSCORE', 'DTWEXBGS_ZSCORE', 'DTWEXAFEGS_ZSCORE', 'DTWEXEMEGS_ZSCORE',
    'NFCI_ZSCORE', 'STLFSI4_ZSCORE', 'UMCSENT_ZSCORE',
    'ICSA_ZSCORE', 'CCSA_ZSCORE', 'T10YIE_ZSCORE', 'EFFR_ZSCORE', 'SOFR_ZSCORE',
    'TOTRESNS_ZSCORE',
    
    // Rate of Change
    'WALCL_ROC', 'RRPONTSYD_ROC', 'DTWEXBGS_ROC', 'DCOILWTICO_ROC', 'DHHNGSP_ROC', 'CCSA_ROC', 'NET_LIQUIDITY_ROC',
    
    // Percentiles
    'VIXCLS_PCT', 'DGS10_PCT', 'DTWEXBGS_PCT', 'BAMLH0A0HYM2_PCT', 'DCOILWTICO_PCT', 'NET_LIQUIDITY_PCT',
    
    // Moving Averages
    'VIXCLS_MA20', 'ICSA_MA4', 'DCOILWTICO_MA50',
  ],
};

/**
 * Check if a signal is derived (computed from source data)
 * @param {string} signalId
 * @returns {boolean}
 */
export function isCapComputed(signalId) {
  return SIGNAL_CLASSIFICATION.cap_computed.includes(signalId);
}

/**
 * Check if a signal has a history endpoint on the backend.
 * Derived signals (z-scores, ROC, percentiles, MAs) are computed on-the-fly
 * and don't have historical data stored - only source/raw FRED data has history.
 * 
 * @param {string} signalId - Signal ID to check
 * @returns {boolean} True if the signal has a history endpoint
 */
export function hasHistoryEndpoint(signalId) {
  // Derived signals don't have history endpoints
  if (SIGNAL_CLASSIFICATION.cap_computed.includes(signalId)) {
    return false;
  }
  
  // Additional pattern-based filtering for any signals not in the list
  // but following derived naming conventions
  const id = signalId.toUpperCase();
  if (id.endsWith('_ZSCORE') || id.endsWith('_Z')) return false;
  if (id.endsWith('_ROC') || id.endsWith('_CHANGE') || id.endsWith('_CHG')) return false;
  if (id.endsWith('_PCT') || id.endsWith('_PERCENTILE')) return false;
  if (id.includes('_MA') && /_(MA\d+|MA)$/.test(id)) return false;
  
  // Source signals have history
  return true;
}

/**
 * Get list of signal IDs that have history endpoints
 * @returns {string[]} Array of signal IDs with history
 */
export function getSignalsWithHistory() {
  return ALL_SIGNAL_IDS.filter(hasHistoryEndpoint);
}

/**
 * Formulas for derived signals
 * Tiered transparency: Show basic transforms, protect proprietary methodology
 */
export const SIGNAL_FORMULAS = {
  // Composite - show formula
  NET_LIQUIDITY: { formula: 'WALCL − WTREGEN − RRPONTSYD', showFormula: true },
  REAL_5Y: { formula: 'DGS5 − T5YIE', showFormula: true },
  REAL_10Y: { formula: 'DGS10 − T10YIE', showFormula: true },
  VIX_TERM_SPREAD: { formula: 'VIX3M − VIX', showFormula: true },
  
  // Spreads - show formula
  CURVE_2S10S: { formula: 'DGS10 − DGS2', showFormula: true },
  CURVE_3M10Y: { formula: 'DGS10 − DGS3MO', showFormula: true },
  HY_IG_SPREAD: { formula: 'HY OAS − IG OAS', showFormula: true },
  SOFR_EFFR_SPREAD: { formula: 'SOFR − EFFR', showFormula: true },
  EFFR_IORB_SPREAD: { formula: 'EFFR − IORB', showFormula: true },
  CCC_B_SPREAD: { formula: 'CCC yield − B yield', showFormula: true },
  BBB_TREASURY_SPREAD: { formula: 'BBB yield − Treasury', showFormula: true },
  
  // TERM_PREMIUM_PROXY - partial transparency (proprietary)
  TERM_PREMIUM_PROXY: { formula: 'Proprietary calculation', showFormula: false },
};

/**
 * Generic formula patterns for suffixed signals
 */
const GENERIC_FORMULAS = {
  _ZSCORE: { formula: '(value − mean) / σ', showFormula: true },
  _ROC: { formula: '% change over period', showFormula: true },
  _PCT: { formula: 'Historical percentile rank', showFormula: true },
  _MA: { formula: 'N-day moving average', showFormula: true },
};

/**
 * Get formula for a signal (respects tiered transparency)
 * @param {string} signalId
 * @returns {{ formula: string, showFormula: boolean } | null}
 */
export function getSignalFormula(signalId) {
  // Direct match first
  if (SIGNAL_FORMULAS[signalId]) {
    return SIGNAL_FORMULAS[signalId];
  }
  // Check generic suffixes
  if (signalId.endsWith('_ZSCORE')) return GENERIC_FORMULAS._ZSCORE;
  if (signalId.endsWith('_ROC')) return GENERIC_FORMULAS._ROC;
  if (signalId.endsWith('_PCT')) return GENERIC_FORMULAS._PCT;
  if (signalId.includes('_MA')) return GENERIC_FORMULAS._MA;
  return null;
}

// ============================================================================
// UTILITIES
// ============================================================================

export function toChartData(historyData) {
  if (!historyData || !Array.isArray(historyData)) return [];
  return historyData
    .filter(point => point.value != null)
    .map(point => ({
      time: typeof point.date === 'string' 
        ? Math.floor(new Date(point.date).getTime() / 1000)
        : point.date,
      value: point.value
    }))
    .sort((a, b) => a.time - b.time);
}

export function getLevelColor(level) {
  const colors = { 
    low: 'var(--tg-fog-green)', 
    normal: 'var(--tg-fog-green)', 
    elevated: '#f97316', 
    high: 'var(--tg-rust-red)', 
    extreme: 'var(--tg-flash-crash)',
    tight: 'var(--tg-fog-green)',
    wide: '#f97316',
    stressed: 'var(--tg-rust-red)',
    inverted: 'var(--tg-rust-red)',
    flat: '#f97316',
    steep: 'var(--tg-fog-green)',
    calm: 'var(--tg-fog-green)',
    neutral: 'var(--tg-lantern-gold)',
    crisis: 'var(--tg-flash-crash)',
  };
  return colors[level] || 'var(--tg-text-faint)';
}

export function getLevelFromValue(value, thresholds) {
  if (!thresholds || value == null) return 'normal';
  if (thresholds.extreme !== undefined) {
    if (value >= thresholds.extreme) return 'extreme';
    if (value >= thresholds.high) return 'high';
    if (value >= thresholds.elevated) return 'elevated';
    if (value <= thresholds.low) return 'low';
    return 'normal';
  }
  return 'normal';
}

// ============================================================================
// PRELOAD - Enriches batch response with client-side metadata
// ============================================================================

export async function preloadAllSignals() {
  try {
    const batchResponse = await fetchSignalsBatch(ALL_SIGNAL_IDS);
    console.log('[API] Batch response:', batchResponse);
    
    const batchValues = batchResponse.data || {};
    
    // Identify signals with no data
    const signalsWithData = Object.keys(batchValues).filter(id => batchValues[id]?.value !== undefined && batchValues[id]?.value !== null);
    const signalsWithoutData = ALL_SIGNAL_IDS.filter(id => !batchValues[id] || batchValues[id]?.value === undefined || batchValues[id]?.value === null);
    
    console.log(`[API] Signals with data: ${signalsWithData.length}`);
    console.log(`[API] Signals without data (${signalsWithoutData.length}):`, signalsWithoutData);
    
    // Also check for signals with value but no level
    const signalsNoLevel = Object.entries(batchValues)
      .filter(([id, data]) => data?.value !== undefined && !data?.level)
      .map(([id]) => id);
    if (signalsNoLevel.length > 0) {
      console.warn(`[API] Signals with value but no level:`, signalsNoLevel);
    }
    
    // Organize by category and enrich with metadata
    const organized = {};
    for (const [category, signalIds] of Object.entries(SIGNAL_IDS)) {
      organized[category] = signalIds.map(signalId => {
        const currentValue = batchValues[signalId] || null;
        const metadata = SIGNAL_METADATA[signalId] || {};
        
        return {
          signal_id: signalId,
          name: metadata.name || signalId.replace(/_/g, ' '),
          description: metadata.description || null,
          formula: metadata.formula || null,
          category: category,
          tier: metadata.tier || 'free',
          frequency: metadata.frequency || 'Daily',
          units: metadata.units || null,
          inputs: metadata.inputs || [],
          thresholds: metadata.thresholds || null,
          current: currentValue ? {
            date: currentValue.date,
            value: currentValue.value,
            level: currentValue.level || 'normal',
            percentile: currentValue.percentile,
            trend: currentValue.trend,
          } : null,
        };
      });
    }
    
    console.log('[API] Organized signals:', organized);
    
    return {
      success: true,
      signals: organized,
      failed: batchResponse.meta?.missing || [],
      timestamp: new Date().toISOString(),
    };
  } catch (error) {
    console.error('[API] Batch fetch failed:', error);
    return {
      success: false,
      signals: {},
      failed: ALL_SIGNAL_IDS,
      error: error.message,
      timestamp: new Date().toISOString(),
    };
  }
}

export async function preloadHistory(signalIds, days = 30) {
  const results = {};
  await Promise.allSettled(
    signalIds.map(async (signalId) => {
      try {
        const response = await fetchSignalHistory(signalId, { days });
        const historyData = response.data || [];
        results[signalId] = { success: true, data: historyData, chartData: toChartData(historyData) };
      } catch (error) {
        results[signalId] = { success: false, error: error.message, data: [], chartData: [] };
      }
    })
  );
  return results;
}

export { CONFIG, CONFIG as API_CONFIG };
